import React, { useEffect, useMemo, useState } from "react";
import { geoAlbersUsa, geoConicConformal, geoMercator, geoPath } from "d3-geo";
import { feature } from "topojson-client";

const W = 960, H = 520;
const URLS = {
  US: "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json",
  CA: "https://cdn.jsdelivr.net/gh/codeforgermany/click_that_hood@main/public/data/canada.geojson",
  AU: "https://cdn.jsdelivr.net/gh/codeforgermany/click_that_hood@main/public/data/australia.geojson",
};
const _cache = {};

// linear hex interpolation cool -> hot
function lerpHex(a, b, t) {
  const pa = [parseInt(a.slice(1, 3), 16), parseInt(a.slice(3, 5), 16), parseInt(a.slice(5, 7), 16)];
  const pb = [parseInt(b.slice(1, 3), 16), parseInt(b.slice(3, 5), 16), parseInt(b.slice(5, 7), 16)];
  const c = pa.map((v, i) => Math.round(v + (pb[i] - v) * t));
  return `#${c.map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

const propName = (p = {}) => p.name || p.NAME_1 || p.PRENAME || p.gn_name || p.NAME || "";

export default function GeoHeatMap({ country = "US", states = [], onSelect, money, hot = "#EF4444", cool = "#14324F", metric = "leak" }) {
  const [topo, setTopo] = useState(_cache[country] || null);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    let alive = true;
    if (_cache[country]) { setTopo(_cache[country]); return; }
    if (!URLS[country]) { setTopo(null); return; }
    fetch(URLS[country]).then((r) => r.json()).then((j) => {
      _cache[country] = j;
      if (alive) setTopo(j);
    }).catch(() => {});
    return () => { alive = false; };
  }, [country]);

  const byName = useMemo(() => {
    const m = {};
    states.forEach((s) => { m[s.state.toLowerCase()] = s; });
    return m;
  }, [states]);
  const valOf = (d) => (metric === "win" ? (d.reconciled || 0) : (d.leakage || 0));
  const maxVal = useMemo(() => Math.max(1, ...states.map(valOf)), [states, metric]); // eslint-disable-line react-hooks/exhaustive-deps

  const { features, path } = useMemo(() => {
    if (!topo) return { features: [], path: null };
    let fc;
    if (topo.type === "FeatureCollection") {
      fc = topo;
    } else {
      const objKey = country === "US" ? "states" : Object.keys(topo.objects)[0];
      fc = feature(topo, topo.objects[objKey]);
    }
    const feats = fc.features || [];
    const proj = (country === "US"
      ? geoAlbersUsa()
      : country === "NZ" || country === "AU"
      ? geoMercator()
      : geoConicConformal().parallels([49, 77]).rotate([95, 0])
    ).fitSize([W, H], fc);
    return { features: feats, path: geoPath(proj) };
  }, [topo, country]);

  const fillFor = (name) => {
    const d = byName[name.toLowerCase()];
    if (!d) return "#232C40";
    const t = Math.pow(valOf(d) / maxVal, 0.6); // gamma for nicer spread
    return lerpHex(cool, hot, t);
  };

  return (
    <div className="relative" data-testid="geo-heatmap">
      {!topo && <div className="h-[320px] flex items-center justify-center text-sm text-slate-400">{URLS[country] ? "Loading map…" : "Browse the ranked cards below."}</div>}
      {topo && path && (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label={`${country} unaccounted-revenue heatmap`}>
          <g>
            {features.map((geo, i) => {
              const name = propName(geo.properties);
              const d = byName[name.toLowerCase()];
              const isHover = hover && hover.name === name;
              return (
                <path
                  key={geo.id || i}
                  d={path(geo)}
                  data-testid={d ? `map-state-${d.code || name}` : undefined}
                  onMouseMove={(e) => d && setHover({ name, x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY, d })}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => d && onSelect && onSelect(d.state)}
                  style={{ cursor: d ? "pointer" : "default", transition: "opacity 150ms, stroke 150ms" }}
                  fill={fillFor(name)}
                  stroke={isHover ? "#38BDF8" : "#0E131F"}
                  strokeWidth={isHover ? 1.6 : 0.6}
                  opacity={isHover ? 0.95 : 1}
                />
              );
            })}
          </g>
        </svg>
      )}

      {/* Tooltip */}
      {hover && hover.d && (
        <div
          className="pointer-events-none absolute z-20 rounded-lg border border-[#2A3550] bg-[#171E2E]/95 px-3 py-2 text-xs shadow-xl"
          style={{ left: Math.min(hover.x + 14, W - 160), top: hover.y + 12, maxWidth: 200 }}
          data-testid="heatmap-tooltip"
        >
          <div className="font-medium text-slate-100">{hover.name}</div>
          <div className="text-slate-400">{hover.d.stores} store{hover.d.stores !== 1 ? "s" : ""} · {hover.d.flagged} flags</div>
          <div className="mt-1 flex items-center gap-3">
            <span style={{ color: "#34D399" }}>{money(hover.d.reconciled)}</span>
            <span style={{ color: hot }}>{money(hover.d.leakage)}</span>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="mt-3 flex items-center gap-3 text-xs text-slate-400" data-testid="heatmap-legend">
        <span>Low</span>
        <span className="h-2.5 w-40 rounded-full" style={{ background: `linear-gradient(90deg, ${cool}, ${hot})` }} />
        <span>{metric === "win" ? "High matched sales" : "High potential leakage"}</span>
        <span className="ml-auto text-slate-500">Hover a state for detail · click to drill in</span>
      </div>
    </div>
  );
}
